import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Copy, UploadCloud, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

const MAX_MB = 6;
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];

// Downscale + recompress on the client so brand photography uploads stay
// within our server-side limit and load quickly on the public site.
async function compressImage(file, { maxDim = 2200, quality = 0.86 } = {}) {
    if (!file.type.startsWith('image/')) return file;
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
    if (file.size < 400 * 1024) return file;
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, w, h);
        const outMime = file.type === 'image/png' ? 'image/jpeg' : file.type;
        const blob = await new Promise((res) => canvas.toBlob(res, outMime, quality));
        if (!blob || blob.size >= file.size) return file;
        const ext = outMime === 'image/jpeg' ? 'jpg' : (file.name.split('.').pop() || 'jpg');
        return new File([blob], `${(file.name || 'upload').replace(/\.[^.]+$/, '')}.${ext}`, { type: outMime });
    } catch {
        return file;
    }
}

async function uploadFile(rawFile) {
    if (!ALLOWED_MIMES.includes(rawFile.type)) {
        throw new Error(`Unsupported file type: ${rawFile.type || 'unknown'}`);
    }
    if (rawFile.size > MAX_MB * 1024 * 1024 * 3) {
        throw new Error(`File too large. Please keep it under ${MAX_MB * 3} MB.`);
    }
    const file = await compressImage(rawFile);
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/admin/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res?.data?.url;
}

function niceNameFromFilename(name) {
    return (name || 'Untitled')
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (c) => c.toUpperCase()) || 'Untitled';
}

export default function AdminGalleryPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: '', url: '', tags: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [dialogUploading, setDialogUploading] = useState(false);
    const [dropUploading, setDropUploading] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const dropInputRef = useRef(null);
    const dialogInputRef = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get('/gallery');
            setItems(r.data.items || []);
        } catch {
            toast.error('Failed to load');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        if (form.name.trim().length < 1 || !form.url.trim()) {
            toast.error('Name and URL are required.');
            return;
        }
        try {
            setSaving(true);
            await api.post('/admin/gallery', {
                name: form.name,
                url: form.url,
                description: form.description,
                tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
            });
            toast.success('Added to gallery');
            setOpen(false);
            setForm({ name: '', url: '', tags: '', description: '' });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to add');
        } finally { setSaving(false); }
    };

    // Bulk drop → upload each file, auto-create a gallery entry per file
    const handleBulkFiles = useCallback(async (files) => {
        const list = Array.from(files || []);
        if (list.length === 0) return;
        let added = 0;
        setDropUploading(list.length);
        for (const f of list) {
            try {
                const url = await uploadFile(f);
                if (!url) throw new Error('Upload returned no URL');
                await api.post('/admin/gallery', {
                    name: niceNameFromFilename(f.name),
                    url,
                    description: '',
                    tags: ['upload'],
                });
                added += 1;
            } catch (err) {
                toast.error(`${f.name}: ${err?.response?.data?.detail || err?.message || 'Failed'}`);
            } finally {
                setDropUploading((n) => Math.max(0, n - 1));
            }
        }
        if (added > 0) {
            toast.success(`${added} image${added > 1 ? 's' : ''} added to gallery.`);
            load();
        }
    }, []);

    const onDropZoneDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        handleBulkFiles(e.dataTransfer?.files);
    }, [handleBulkFiles]);

    // Dialog: upload single file → fills url field
    const handleDialogFile = useCallback(async (file) => {
        if (!file) return;
        try {
            setDialogUploading(true);
            const url = await uploadFile(file);
            setForm((prev) => ({
                ...prev,
                url,
                name: prev.name || niceNameFromFilename(file.name),
            }));
            toast.success('File uploaded. Add metadata and save.');
        } catch (err) {
            toast.error(err?.message || 'Upload failed');
        } finally {
            setDialogUploading(false);
        }
    }, []);

    const remove = async (id) => {
        if (!window.confirm('Remove this image?')) return;
        try { await api.delete(`/admin/gallery/${id}`); toast.success('Removed'); load(); } catch { toast.error('Failed'); }
    };

    const copy = (url) => {
        // Copy an absolute URL when we have an /api/uploads/ relative one
        const absolute = url.startsWith('/') ? `${window.location.origin}${url}` : url;
        navigator.clipboard?.writeText(absolute)
            .then(() => toast.success('URL copied'))
            .catch(() => toast.error('Copy failed'));
    };

    return (
        <div data-testid="admin-gallery-page" className="space-y-6">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Gallery</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Drop images (JPG, PNG, WebP, GIF, SVG · up to {MAX_MB} MB) or paste public URLs. Files upload directly to your workspace so they survive redeploys.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm({ name: '', url: '', tags: '', description: '' }); }}>
                    <DialogTrigger asChild>
                        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-gallery-new-button"><Plus className="h-4 w-4 mr-1" /> Add image</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg bg-[rgba(8,12,20,0.95)] border-white/10 text-foreground">
                        <DialogHeader><DialogTitle className="font-display">Add image</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            {/* File upload area inside the dialog */}
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">Upload file</Label>
                                <div
                                    onClick={() => dialogInputRef.current?.click()}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); handleDialogFile(e.dataTransfer?.files?.[0]); }}
                                    className="mt-1.5 border border-dashed border-white/15 hover:border-primary/50 rounded-lg p-4 text-center cursor-pointer bg-white/5 transition-colors"
                                    data-testid="gallery-dialog-drop"
                                >
                                    {dialogUploading ? (
                                        <div className="inline-flex items-center gap-2 text-primary text-sm">
                                            <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                                        </div>
                                    ) : (
                                        <div className="inline-flex items-center gap-2 text-foreground/80 text-sm">
                                            <UploadCloud className="h-4 w-4 text-primary" />
                                            <span>Drop a file here, or click to pick</span>
                                        </div>
                                    )}
                                    <input
                                        ref={dialogInputRef}
                                        type="file"
                                        accept={ALLOWED_MIMES.join(',')}
                                        className="hidden"
                                        onChange={(e) => { handleDialogFile(e.target?.files?.[0]); e.target.value = ''; }}
                                        data-testid="gallery-dialog-file-input"
                                    />
                                </div>
                            </div>

                            <Field label="Name">
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10" data-testid="gallery-name-input" />
                            </Field>
                            <Field label="Image URL">
                                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://… or upload above" className="bg-white/5 border-white/10 font-mono text-xs" data-testid="gallery-url-input" />
                            </Field>
                            <Field label="Tags (comma separated)">
                                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="hero, blog, services" className="bg-white/5 border-white/10" />
                            </Field>
                            <Field label="Description (optional)">
                                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10" />
                            </Field>
                            {form.url && (
                                <div className="rounded-lg overflow-hidden border border-white/10 relative">
                                    <img src={form.url} alt="preview" className="w-full aspect-[16/9] object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
                                    <button
                                        type="button"
                                        onClick={() => setForm((p) => ({ ...p, url: '' }))}
                                        className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/60 border border-white/10 flex items-center justify-center hover:bg-black/80"
                                        title="Clear URL"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button onClick={add} disabled={saving || dialogUploading} data-testid="gallery-save-button" className="bg-primary text-primary-foreground hover:bg-primary/90">{saving ? 'Saving…' : 'Save'}</Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </header>

            {/* Big drop zone for quick multi-upload */}
            <div
                onClick={() => dropInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                onDrop={onDropZoneDrop}
                className={`rounded-2xl border-2 border-dashed p-6 sm:p-8 cursor-pointer text-center transition-all ${
                    dragOver
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/40'
                        : 'border-white/10 hover:border-primary/50 bg-white/5'
                }`}
                data-testid="gallery-bulk-drop"
                role="button"
                aria-label="Drop images to upload"
            >
                {dropUploading > 0 ? (
                    <div className="inline-flex items-center gap-2 text-primary">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="font-mono uppercase tracking-widest text-xs">Uploading {dropUploading} file{dropUploading > 1 ? 's' : ''}…</span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2 text-foreground/85">
                        <UploadCloud className="h-6 w-6 text-primary" />
                        <div className="text-sm font-medium">
                            {dragOver ? 'Drop to upload' : 'Drop images here, or click to pick'}
                        </div>
                        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                            Multiple files supported · Auto-compressed · Kept in-workspace
                        </div>
                    </div>
                )}
                <input
                    ref={dropInputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_MIMES.join(',')}
                    className="hidden"
                    onChange={(e) => { handleBulkFiles(e.target?.files); e.target.value = ''; }}
                    data-testid="gallery-bulk-file-input"
                />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {loading ? (
                    <div className="col-span-full py-10 text-center text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="col-span-full py-16 text-center text-muted-foreground border border-dashed border-white/10 rounded-2xl">
                        No images yet. Drop files above, or click “Add image” to include metadata.
                    </div>
                ) : (
                    items.map((it) => (
                        <div key={it.id} className="dt-glass rounded-xl overflow-hidden group" data-testid="gallery-item">
                            <div className="aspect-[4/3] overflow-hidden bg-white/5">
                                <img src={it.url} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            </div>
                            <div className="p-3">
                                <div className="text-sm font-medium truncate">{it.name}</div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {(it.tags || []).slice(0, 3).map((t) => (
                                        <span key={t} className="text-[10px] font-mono uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded">{t}</span>
                                    ))}
                                </div>
                                <div className="mt-3 flex items-center justify-between">
                                    <Button size="sm" variant="ghost" onClick={() => copy(it.url)}><Copy className="h-3.5 w-3.5 mr-1" /> Copy URL</Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(it.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">{label}</Label>{children}</div>;
}
